import React, { useRef, forwardRef, useState } from "react";
import { Button } from "flowbite-react";
import { FaDownload, FaEye } from "react-icons/fa";
import { BiSave } from "react-icons/bi";
import { CgSpinner } from "react-icons/cg";
import { uploadToCloudinary } from "../../Utils/cloudinaryUpload";
import { useMakeRequest } from "../../hooks/useMakeRequest";
import { JOBSEEKER_URL } from "../../resources/resource";
import { toast } from "sonner";
import { useSelector } from "react-redux";

// Create a forwardRef wrapper for the content
const ContentWrapper = forwardRef(({ children }, ref) => (
  <div ref={ref} className="w-full">
    {children}
  </div>
));

ContentWrapper.displayName = "ContentWrapper";

const PDFGenerator = ({ children }) => {
  const contentRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSending, setIsSending] = useState(false);
  const makeRequest = useMakeRequest();
  const loggedInUser = useSelector((state) => state.user);
  console.log("user", loggedInUser.currentUser._id);

  const generatePDF = async () => {
    if (!contentRef.current) return;
    setIsLoading(true);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Higher scale for better quality (targeting ~5MB)
      const canvas = await html2canvas(contentRef.current, {
        scale: 1.5, // Increased for better quality
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        removeContainer: true,
        imageTimeout: 0,
        // Additional optimization options
        allowTaint: false,
        foreignObjectRendering: false,
        width: contentRef.current.scrollWidth,
        height: contentRef.current.scrollHeight,
      });

      // Higher quality JPEG for better output
      const imgData = canvas.toDataURL("image/jpeg", 0.85); // Higher quality

      // Use standard A4 size instead of custom dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true, // Enable PDF compression
      });

      // Calculate dimensions to fit A4
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasAspectRatio = canvas.height / canvas.width;
      const pdfAspectRatio = pdfHeight / pdfWidth;

      let imgWidth, imgHeight;
      if (canvasAspectRatio > pdfAspectRatio) {
        imgHeight = pdfHeight;
        imgWidth = imgHeight / canvasAspectRatio;
      } else {
        imgWidth = pdfWidth;
        imgHeight = imgWidth * canvasAspectRatio;
      }

      const x = (pdfWidth - imgWidth) / 2;
      const y = (pdfHeight - imgHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);
      pdf.save("cv-document.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePdfBlob = async () => {
    if (!contentRef.current) return null;

    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(contentRef.current, {
        scale: 1.8,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        removeContainer: true,
        imageTimeout: 0,
        allowTaint: false,
        foreignObjectRendering: false,
        width: contentRef.current.scrollWidth,
        height: contentRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.9);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasAspectRatio = canvas.height / canvas.width;
      const pdfAspectRatio = pdfHeight / pdfWidth;

      let imgWidth, imgHeight;
      if (canvasAspectRatio > pdfAspectRatio) {
        imgHeight = pdfHeight;
        imgWidth = imgHeight / canvasAspectRatio;
      } else {
        imgWidth = pdfWidth;
        imgHeight = imgWidth * canvasAspectRatio;
      }

      const x = (pdfWidth - imgWidth) / 2;
      const y = (pdfHeight - imgHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);

      return pdf.output("blob");
    } catch (error) {
      console.error("Error generating PDF blob:", error);
      return null;
    }
  };

  const previewPDF = async () => {
    if (!contentRef.current) return;
    setIsLoading(true);

    try {
      const pdfBlob = await generatePdfBlob();
      if (pdfBlob) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");

        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 60000); // Clean up after 1 minute
      }
    } catch (error) {
      console.error("Error previewing PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveCV = async () => {
    if (!contentRef.current) return;
    setIsSending(true);

    try {
      const pdfBlob = await generatePdfBlob();

      if (!pdfBlob) {
        throw new Error("Failed to generate PDF");
      }

      const fileSizeMB = pdfBlob.size / (1024 * 1024);

      if (fileSizeMB > 8) {
        alert(
          `File size (${fileSizeMB.toFixed(
            2
          )} MB) is too large. Maximum allowed is 8MB. Please try reducing the content.`
        );
        return;
      }

      if (fileSizeMB < 1) {
        console.warn(
          `File size is quite small (${fileSizeMB.toFixed(
            2
          )} MB). Consider increasing quality if needed.`
        );
      }

      const urlPath = await uploadToCloudinary(pdfBlob);
      makeRequest(
        JOBSEEKER_URL + `/updateJobSeeker/${loggedInUser.currentUser._id}`,
        "PUT",
        { resume: urlPath },
        () => toast.success("uploaded"),
        (error) => console.log(error),
        console.log
      );
    } catch (error) {
      console.error("Error saving CV:", error);
      alert("Failed to save CV. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex gap-4 mb-4">
        <Button onClick={generatePDF} disabled={isLoading} color="primary">
          {isLoading ? (
            <CgSpinner className="animate-spin mr-2 h-4 w-4" />
          ) : (
            <FaDownload className="mr-2 h-4 w-4" />
          )}
          Download PDF
        </Button>
        <Button onClick={previewPDF} disabled={isLoading} color="gray" outline>
          <FaEye className="mr-2 h-4 w-4 mb-1" />
          Preview PDF
        </Button>
        <Button
          className="flex items-center justify-center"
          onClick={saveCV}
          disabled={isSending}
          outline
        >
          {isSending ? (
            <CgSpinner className="animate-spin" />
          ) : (
            <>
              <BiSave className="mr-2 h-4 w-4" />
              Save To Profile
            </>
          )}
        </Button>
      </div>

      <ContentWrapper ref={contentRef}>{children}</ContentWrapper>
    </div>
  );
};

export default PDFGenerator;
